/**
 * UpgradeNudgeCard — inline, never-modal upgrade prompt.
 *
 * PLANS-1B rewrite. Spec §22: "No paywall popups. Contextual nudges
 * live inline where the studio hits the constraint." Same component
 * used from Storage, Team management, client / project add gates,
 * automation, analytics, custom branding, custom domain, and video
 * proofing — so the voice + accent stay consistent.
 *
 * Copy tone is Rennan's marketing voice: outcome-led, warm, never
 * "you can't do this." Every message tells the studio what the next
 * tier UNLOCKS, not what the current tier blocks.
 *
 * Reasons removed vs. SUB-1:
 *   • `nova` — Nova is unlimited on every paid tier post-PLANS-1.
 *
 * New reasons added:
 *   • `client_cap` / `project_cap` — Starter caps at 15 clients + 5
 *     active projects; upgrading to Orbit lifts both to unlimited.
 *   • `custom_branding` / `custom_domain` — Orbit is the first tier
 *     with brand-your-app + your-own-domain.
 *   • `advanced_video_proofing` — PLANS-3: Starter is photo
 *     galleries only; video proofing + delivery (1080p) starts at
 *     Orbit, 4K + originals at Business.
 */

import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { NovaExpression } from '@/components/nova/kit';
import {
  PLAN_LABELS,
  type SubscriptionPlan,
} from '@/services/billing';
// P0-1 — three reasons here upsell Orbit Business (`team`,
// `analytics`, `custom_email_domain`), and any reason whose target
// the studio already holds escalates to Business via
// `effectiveTarget` below. Business isn't purchasable for the
// 2026-08-28 launch, so those nudges must stop pointing at a plan
// that isn't on the pricing page.
import { BUSINESS_ON_SALE, BUSINESS_HELD_MESSAGE } from '@/config/launch';

export type UpgradeReason =
  | 'storage'
  | 'team'
  | 'client_cap'
  | 'project_cap'
  | 'automation'
  | 'analytics'
  | 'custom_branding'
  | 'custom_domain'
  | 'custom_email_domain'
  | 'nova_sites'
  | 'advanced_video_proofing';

export interface UpgradeNudgeCardProps {
  /** What triggered the nudge — picks copy + accent. */
  reason: UpgradeReason;
  /** Studio's current plan (for "already on X" copy tuning). */
  currentPlan?: SubscriptionPlan;
  /**
   * Recommended target plan. Defaults per-reason (Orbit for most,
   * Business for team + analytics). Callers can force a specific
   * upsell when they know the ladder they want to guide.
   */
  suggestedPlan?: Exclude<SubscriptionPlan, 'free'>;
  /**
   * Where clicking "See plans" routes to. Defaults to Settings →
   * Billing (works even for signed-in studios who never see /orbit
   * marketing). Callers rendering on the public marketing page
   * (unusual for this component) can override to '#pricing'.
   */
  ctaHref?: string;
  /** Extra wrapper classes. */
  className?: string;
  /** Compact one-line variant — used inside the compact storage widget. */
  compact?: boolean;
}

type ReasonCopy = {
  eyebrow: string;
  title: string;
  body: (target: string) => string;
  novaMood: 'happy' | 'suggestion' | 'curious';
  /** Default upsell target when the caller doesn't override. */
  defaultTarget: Exclude<SubscriptionPlan, 'free'>;
};

const REASON_COPY: Record<UpgradeReason, ReasonCopy> = {
  storage: {
    eyebrow: 'More room',
    title: 'Your galleries are growing.',
    body: (target) =>
      target === 'Orbit Business'
        ? 'Orbit Business gives you unlimited headroom.'
        : `${target} adds more space so your delivery archive keeps up.`,
    novaMood: 'suggestion',
    defaultTarget: 'orbit',
  },
  team: {
    eyebrow: 'Room for more',
    title: 'Your team is coming together.',
    body: () => 'Orbit Business fits everyone — unlimited seats.',
    novaMood: 'suggestion',
    defaultTarget: 'orbit_business',
  },
  client_cap: {
    eyebrow: 'A bigger circle',
    title: 'Your client list is filling up.',
    body: () => 'Add more with Orbit — unlimited clients.',
    novaMood: 'suggestion',
    defaultTarget: 'orbit',
  },
  project_cap: {
    eyebrow: 'More in flight',
    title: 'Your calendar is booking up.',
    body: () => 'Add more with Orbit — unlimited active projects.',
    novaMood: 'suggestion',
    defaultTarget: 'orbit',
  },
  automation: {
    // P1-WEB-1 — copy narrowed. Was "unlocks advanced automation",
    // which cross-sold the killed Workflows builder (#341). Points
    // at what AutomationSettings actually does — rules that fire an
    // email or a team ping when something happens.
    eyebrow: 'Autopilot',
    title: 'Ready to let Orbit run more on autopilot?',
    body: (target) =>
      `${target} adds automation rules — send an email or ping the team the moment an inquiry, project, or delivery moves.`,
    novaMood: 'curious',
    defaultTarget: 'orbit',
  },
  analytics: {
    eyebrow: 'Deeper insight',
    title: 'Ready for the full picture?',
    body: (target) =>
      `${target} adds advanced analytics + trends across every project.`,
    novaMood: 'curious',
    defaultTarget: 'orbit_business',
  },
  custom_branding: {
    eyebrow: 'Your brand, front and center',
    title: 'Put your logo everywhere clients look.',
    body: () =>
      'Orbit adds your logo across the app, every client email, and the portal.',
    novaMood: 'happy',
    defaultTarget: 'orbit',
  },
  custom_domain: {
    eyebrow: 'Your address',
    title: 'Serve your Sites from your own domain.',
    body: () =>
      "Orbit lets you swap orbit's URL for yours — same site, your address.",
    novaMood: 'happy',
    defaultTarget: 'orbit',
  },
  // PLAN-2 — custom EMAIL sending domains are Orbit Business only.
  custom_email_domain: {
    eyebrow: 'From your inbox',
    title: 'Send client emails from your own domain.',
    body: () =>
      'Orbit Business lets every client email arrive from your address — hello@yourstudio.com instead of Orbit’s.',
    novaMood: 'happy',
    defaultTarget: 'orbit_business',
  },
  // PLAN-2 — Nova-designed sites unlock on Orbit (middle tier).
  // Starter keeps templates + intake forms.
  nova_sites: {
    eyebrow: 'Nova, site designer',
    title: 'Let Nova design your site for you.',
    body: () =>
      'Templates + intake forms are included on your plan; Nova-designed sites unlock on Orbit — describe your business in a sentence and she builds the pages.',
    novaMood: 'happy',
    defaultTarget: 'orbit',
  },
  advanced_video_proofing: {
    eyebrow: 'Films, delivered',
    title: 'Send finished films the way you send galleries.',
    body: () =>
      'Orbit adds video delivery — streamed in 1080p, with client notes pinned to the frame. Business adds 4K and the original files.',
    novaMood: 'suggestion',
    defaultTarget: 'orbit',
  },
};

export function UpgradeNudgeCard({
  reason,
  currentPlan,
  suggestedPlan,
  ctaHref = '/settings?tab=billing',
  className,
  compact = false,
}: UpgradeNudgeCardProps) {
  const navigate = useNavigate();
  const copy = REASON_COPY[reason];
  const target = suggestedPlan ?? copy.defaultTarget;

  // Studios already on the recommended tier get a "next-tier" nudge
  // (Orbit → Orbit Business) instead of self-referencing. Keeps this
  // component safe to render at any plan level without an awkward
  // "upgrade to your current plan" bug.
  const effectiveTarget =
    currentPlan && currentPlan === target
      ? 'orbit_business'
      : target;
  const effectiveLabel = PLAN_LABELS[effectiveTarget];

  // P0-1 — the nudge resolved to a tier that isn't on sale. Swap the
  // "here's what X unlocks" line for the true one and drop the CTA:
  // "See plans" would land the studio on a billing page where Orbit
  // Business isn't listed, which is a dead end wearing a button.
  const businessHeld = effectiveTarget === 'orbit_business' && !BUSINESS_ON_SALE;

  const body = businessHeld ? BUSINESS_HELD_MESSAGE : copy.body(effectiveLabel);
  const showCta = currentPlan !== 'orbit_business' && !businessHeld;

  const handleClick = () => {
    if (ctaHref.startsWith('/')) {
      navigate(ctaHref);
    } else {
      window.location.href = ctaHref;
    }
  };

  if (compact) {
    return (
      <div
        className={cn(
          'flex items-center gap-3 rounded-xl border border-orbit-orange/25 bg-orbit-orange/5 px-3 py-2',
          className,
        )}
        role="note"
      >
        <NovaExpression
          id={copy.novaMood}
          size={32}
          pop={false}
          className="shrink-0"
        />
        <p className="flex-1 text-xs text-foreground/85 leading-snug">
          <span className="font-medium text-foreground">{copy.title}</span>{' '}
          <span className="text-foreground/70">{body}</span>
        </p>
        {showCta && (
          <Button
            size="sm"
            variant="ghost"
            onClick={handleClick}
            className="shrink-0 h-7 rounded-full px-3 text-xs text-orbit-orange hover:bg-orbit-orange/10 hover:text-orbit-orange"
          >
            See plans
            <OrbitIcon name="arrow.right" fallback={ArrowRight} className="ml-1 h-3 w-3" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-2xl border border-orbit-orange/25 bg-orbit-orange/[.06] p-4 sm:flex-row sm:items-center sm:gap-5 sm:p-5',
        className,
      )}
      role="region"
      aria-label="Upgrade suggestion"
    >
      <div className="shrink-0">
        <NovaExpression id={copy.novaMood} size={64} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="orbit-eyebrow text-orbit-orange">{copy.eyebrow}</p>
        <p className="mt-1 text-sm font-medium text-foreground">
          {copy.title}
        </p>
        <p className="mt-0.5 text-sm text-foreground/75 leading-snug">
          {body}
        </p>
      </div>
      {showCta && (
        <Button
          size="sm"
          onClick={handleClick}
          className="shrink-0 bg-orbit-orange text-background hover:bg-orbit-orange/90"
        >
          See plans
          <OrbitIcon name="arrow.right" fallback={ArrowRight} className="ml-1 h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

export default UpgradeNudgeCard;
